import { EditButton } from '@features/user-account/components/EditButton';
import { UserInfoList } from '@features/user-account/components/UserInfoList';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { Organization } from '@protos/organization';
import InlineGroup from '@shared/components/InlineGroup';
import MultilineGroup from '@shared/components/MultilineGroup';
import RoundedBox from '@shared/components/RoundedBox';
import { TextInputReadOnlyBox } from '@shared/components/TextInputReadOnlyBox';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useScreenSize } from '@shared/hooks/useScreenSize';
import { chunkArray } from '@shared/utils/array';
import { checkIsAdminReadOnlyUser } from '@utils/permissions';
import { useEffect, useState } from 'react';
import { EditOrganizationTypesDialog } from './components/EditOrganizationTypesDialog';
import { OrganizationLinks } from './components/OrganizationLinks';
import { OrganizationTradeLimits } from './components/OrganizationTradeLimits';

type Props = {
  organization: Organization;
};

// Zoho owns every field here. Only `types` is Flux's, and it has its own dialog.
const generateOrganizationInfo = (organization: Organization) => [
  {
    label: 'Id',
    value: `${organization.id}`,
  },
  {
    label: 'Name',
    value: organization.name || undefined,
  },
  {
    label: 'Zoho organization id',
    value: organization.zoho_organization_id || undefined,
  },
  {
    label: 'CME firm name',
    value: organization.cme_firm_name || undefined,
  },
  {
    label: 'CME firm id',
    value: organization.cme_firm_id || undefined,
  },
  {
    label: 'CME firm name id',
    value: organization.cme_firm_name_id || undefined,
  },
];

const getInputFields = (organization: Organization) => {
  return generateOrganizationInfo(organization).map((data, idx) => (
    <TextInputReadOnlyBox label={data.label} value={data.value ?? '-'} key={`organization_info_${idx}`} />
  ));
};

export const OrganizationDetailForm: React.FC<Props> = ({ organization: organizationProp }) => {
  const { isMobile } = useScreenSize();
  const userViewing = useUserContext();
  const numberOfColumns = isMobile ? 1 : 2;

  const [organization, setOrganization] = useState(organizationProp);
  const [inputs, setInputs] = useState(getInputFields(organizationProp));
  const [isEditTypesDialogOpen, setIsEditTypesDialogOpen] = useState(false);
  // Trade limit product scope comes from the links, so a link change has to re-resolve it
  const [linksVersion, setLinksVersion] = useState(0);

  const canEdit = !checkIsAdminReadOnlyUser(userViewing);

  useEffect(() => {
    setOrganization(organizationProp);
  }, [organizationProp]);

  useEffect(() => {
    setInputs(getInputFields(organization));
  }, [organization]);

  const chunks = chunkArray(inputs, numberOfColumns);

  return (
    <>
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Paper style={{ padding: 20 }}>
          <MultilineGroup>
            <Typography variant="h4" gutterBottom sx={{ marginBottom: '20px' }}>
              Organization
            </Typography>
            {chunks.map((chunk, idx) => (
              <InlineGroup key={`group_${idx}`}>{chunk}</InlineGroup>
            ))}
          </MultilineGroup>
        </Paper>
        <Paper style={{ padding: 20 }}>
          <RoundedBox>
            <UserInfoList
              list={organization.types ?? []}
              header={'Types'}
              helperText={'What Flux lets this organization act as. AGENT is what grants authority; PRINCIPAL is a label'}
              showDivider={true}
              extraOptions={canEdit ? <EditButton onClick={() => setIsEditTypesDialogOpen(true)} /> : undefined}
            />
            <UserInfoList
              list={(organization.trading_accounts ?? []).map(account => account.toLocaleUpperCase())}
              header={'Trading Accounts'}
              showDivider={true}
            />
            <OrganizationLinks organization={organization} canEdit={canEdit} onLinksChanged={() => setLinksVersion(version => version + 1)} />
            <Divider sx={{ margin: '20px 0' }} />
            <OrganizationTradeLimits organization={organization} canEdit={canEdit} linksVersion={linksVersion} />
          </RoundedBox>
        </Paper>
      </Box>
      <EditOrganizationTypesDialog
        organization={organization}
        isOpen={isEditTypesDialogOpen}
        onClose={() => setIsEditTypesDialogOpen(false)}
        onOrganizationUpdated={setOrganization}
      />
    </>
  );
};
